import type { AssetProfile, SecondOrderDomainKey, Storyline } from '@akem/shared';
import CloseIcon from '@mui/icons-material/Close';
import InsightsIcon from '@mui/icons-material/Insights';
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Sparkline } from '@/components/Sparkline.js';
import { getProfileStorylines } from '@/services/intelligence.js';
import type { StorylineSummary } from '@/services/intelligence.js';
import { getStorylineById } from '@/services/feed.js';
import { getProfile } from '@/services/profile.js';
import { StorylineDetails } from '../intelligence/components/StorylineDetails.tsx';
import { useStorylineChatContext } from '../intelligence/components/StorylineChatContext.tsx';
import { StorylineChat } from '../intelligence/components/StorylineChat.tsx';

const DOMAIN_ORDER: SecondOrderDomainKey[] = [
  'marketTrends',
  'politics',
  'regulatory',
  'mna',
  'logistics',
  'customers',
  'suppliers',
  'themes',
];

const DOMAIN_LABELS: Record<SecondOrderDomainKey, string> = {
  marketTrends: 'Market Trends',
  politics: 'Politics',
  regulatory: 'Regulatory',
  mna: 'M&A',
  logistics: 'Logistics',
  customers: 'Customers',
  suppliers: 'Suppliers',
  themes: 'Themes',
};

const STORYLINE_SPARKLINE_COLOR = '#8B66FF';

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const normalizeDomain = (value?: string | null): SecondOrderDomainKey | null => {
  if (!value) return null;
  const key = value.trim() as SecondOrderDomainKey;
  return DOMAIN_ORDER.includes(key) ? key : null;
};

const formatTimestamp = (ts?: number | null): string => {
  if (!ts) return 'Timestamp unavailable';
  try {
    return dateFormatter.format(new Date(ts));
  } catch {
    return 'Timestamp unavailable';
  }
};

export const IntelligenceCategoryPage: React.FC = () => {
  const params = useParams<{ id: string; domain: string }>();
  const navigate = useNavigate();
  const routeId = params.id ?? '';
  const requestedDomain = normalizeDomain(params.domain ?? '');

  React.useEffect(() => {
    if (!requestedDomain && routeId) {
      navigate(`/profiles/${encodeURIComponent(routeId)}/intelligence/marketTrends`, { replace: true });
    }
  }, [requestedDomain, routeId, navigate]);

  const [profile, setProfile] = React.useState<AssetProfile | null>(null);
  const [profileLoading, setProfileLoading] = React.useState(true);
  const [profileError, setProfileError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setProfileLoading(true);
        setProfileError(null);
        const data = await getProfile({ idOrTicker: routeId, useMock: false }).catch(() => null);
        if (cancelled) return;
        if (data) {
          setProfile(data);
        } else {
          setProfile(null);
          setProfileError('Profile not found');
        }
      } catch (err) {
        if (!cancelled) {
          setProfile(null);
          setProfileError(err instanceof Error ? err.message : 'Failed to load profile');
        }
      } finally {
        if (!cancelled) {
          setProfileLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeId]);

  // Prefer the canonical profile id when available; fall back to ticker or route id
  const profileKey = profile?.id || profile?.ticker || routeId;
  const domain = requestedDomain;

  const [storylines, setStorylines] = React.useState<StorylineSummary[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [modalSummary, setModalSummary] = React.useState<StorylineSummary | null>(null);
  const [modalStoryline, setModalStoryline] = React.useState<Storyline | null>(null);
  const [modalLoading, setModalLoading] = React.useState(false);
  const [modalError, setModalError] = React.useState<string | null>(null);
  const { setActiveStoryline } = useStorylineChatContext();

  const loadStorylines = React.useCallback(async (): Promise<void> => {
    if (!domain || !profileKey) return;
    setLoading(true);
    setError(null);
    try {
      const summaries = await getProfileStorylines(profileKey, domain, { limit: 6 });
      setStorylines(summaries);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load storylines');
      setStorylines([]);
    } finally {
      setLoading(false);
    }
  }, [domain, profileKey]);

  React.useEffect(() => {
    if (!domain || !profileKey) {
      setStorylines([]);
      return;
    }
    loadStorylines();
  }, [domain, profileKey, loadStorylines]);

  const openStoryline = React.useCallback(async (storyline: StorylineSummary) => {
    const id = typeof storyline?.id === 'string' ? storyline.id.trim() : '';
    if (!id) return;
    setModalSummary(storyline);
    setModalStoryline(null);
    setModalError(null);
    setModalLoading(true);
    try {
      const detail = await getStorylineById(id);
      if (detail) {
        setModalStoryline(detail);
        setActiveStoryline(detail);
      } else {
        setModalError('Storyline not available');
      }
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to load storyline');
    } finally {
      setModalLoading(false);
    }
  }, []);

  const closeModal = React.useCallback(() => {
    setModalSummary(null);
    setModalStoryline(null);
    setModalLoading(false);
    setModalError(null);
    setActiveStoryline(null);
  }, []);

  const profileLabel = React.useMemo(() => {
    if (!profile) return routeId;
    const symbol = profile.ticker ? profile.ticker.toUpperCase() : null;
    return symbol ? `${profile.name ?? profile.id} (${symbol})` : profile.name ?? profile.id;
  }, [profile, routeId]);

  return (
    <Box sx={{ flex: 1, overflowY: 'auto' }}>
      <Container maxWidth="lg" sx={{ py: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Breadcrumbs
          aria-label="breadcrumb"
          separator="›"
          sx={{
            '& .MuiBreadcrumbs-ol': { alignItems: 'center' },
            '& .MuiBreadcrumbs-separator': { color: 'text.secondary' },
            color: 'text.secondary',
            fontSize: '0.875rem',
          }}
        >
          <Link
            component={RouterLink}
            underline="hover"
            color="inherit"
            to="/market"
            sx={{ display: 'inline-flex', alignItems: 'center', fontSize: '0.875rem' }}
          >
            Market Dashboard
          </Link>
          <Link
            component={RouterLink}
            underline="hover"
            color="inherit"
            to={`/profiles/${encodeURIComponent(routeId)}`}
            sx={{ display: 'inline-flex', alignItems: 'center', fontSize: '0.875rem' }}
          >
            {profileLabel}
          </Link>
          <Typography color="text.primary" variant="body2">
            {domain ? DOMAIN_LABELS[domain] : 'Intelligence'}
          </Typography>
        </Breadcrumbs>

        <Stack spacing={1.5}>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InsightsIcon fontSize="small" />
            {domain ? `${DOMAIN_LABELS[domain]} Intelligence` : 'Intelligence'}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {DOMAIN_ORDER.map((entry) => (
              <Chip
                key={entry}
                component={RouterLink}
                to={`/profiles/${encodeURIComponent(routeId)}/intelligence/${entry}`}
                label={DOMAIN_LABELS[entry]}
                clickable
                color={entry === domain ? 'primary' : 'default'}
                variant={entry === domain ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>
        </Stack>

        {profileLoading ? (
          <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Loading profile…
            </Typography>
          </Stack>
        ) : profileError ? (
          <Typography variant="body2" color="error">
            {profileError}
          </Typography>
        ) : null}

        {!profileLoading && !profileError && (
          <Card variant="outlined" sx={{ borderStyle: 'dashed' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Profile ID
              </Typography>
              <Typography variant="body1">{profileKey || 'Unavailable'}</Typography>
            </CardContent>
          </Card>
        )}

        <Divider />

        {loading ? (
          <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Loading storylines…
            </Typography>
          </Stack>
        ) : error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : storylines.length === 0 ? (
          <Stack spacing={1} alignItems="center" sx={{ py: 6 }}>
            <Typography variant="subtitle1">No storylines yet</Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Storylines will appear once the intelligence pipeline emits updates for this category.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {storylines.map((storyline) => {
              const parsedTs = storyline.asOf ? Date.parse(storyline.asOf) : NaN;
              const timestamp = Number.isFinite(parsedTs) ? parsedTs : undefined;
              const metaParts: string[] = [];
              if (storyline.bucket) metaParts.push(`Bucket ${storyline.bucket}`);
              if (storyline.monitorId) metaParts.push(`Monitor ${storyline.monitorId}`);
              if (storyline.eventId) metaParts.push(`Event ${storyline.eventId}`);
              const metaText =
                metaParts.length > 0
                  ? metaParts.join(' • ')
                  : 'Storyline generated from recent intelligence activity.';
              const domainLabel = DOMAIN_LABELS[storyline.domain] ?? storyline.domain;
              return (
                <Card key={storyline.id || `${storyline.instrumentId}-${storyline.asOf}`} variant="outlined">
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        spacing={2}
                        flexWrap="wrap"
                      >
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {formatTimestamp(timestamp)}
                          </Typography>
                          <Typography variant="h6" sx={{ mt: 0.5 }}>
                            {storyline.title}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip size="small" label={domainLabel} />
                          <Chip size="small" variant="outlined" label="Storyline" />
                          {storyline.bucket ? (
                            <Chip size="small" variant="outlined" label={`Bucket: ${storyline.bucket}`} />
                          ) : null}
                        </Stack>
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {metaText}
                      </Typography>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Sparkline snapshot
                        </Typography>
                        <Sparkline
                          data={Array.isArray(storyline.sparkline) ? storyline.sparkline : []}
                          width={160}
                          height={28}
                          color={STORYLINE_SPARKLINE_COLOR}
                        />
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          Domain: {domainLabel}
                        </Typography>
                        <Button variant="contained" size="small" onClick={() => openStoryline(storyline)}>
                          View storyline
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </Container>

      <StorylineChat profileId={profileKey} ticker={profile?.ticker ?? null} />

      <Dialog
        open={Boolean(modalSummary)}
        onClose={closeModal}
        fullWidth
        maxWidth="lg"
        disableEnforceFocus
        disableRestoreFocus
        disableAutoFocus
        PaperProps={{
          sx: {
            width: { xs: '100%', md: 'min(1100px, 92vw)' },
            maxWidth: '92vw',
            height: { xs: '100%', md: '90vh' },
            backgroundImage: 'none',
            backgroundColor: (theme) => theme.palette.background.paper,
            backdropFilter: 'none',
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="h6">
                {modalStoryline?.title ?? modalSummary?.title ?? 'Storyline details'}
              </Typography>
              {modalSummary?.domain ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={DOMAIN_LABELS[modalSummary.domain] ?? modalSummary.domain}
                />
              ) : null}
            </Stack>
            <IconButton size="small" onClick={closeModal}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: 'calc(90vh - 160px)' }}>
          {modalLoading ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading storyline…
              </Typography>
            </Stack>
          ) : modalError ? (
            <Typography variant="body2" color="error">
              {modalError}
            </Typography>
          ) : modalStoryline ? (
            <StorylineDetails storyline={modalStoryline} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Storyline not available.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default IntelligenceCategoryPage;
